import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseLocalDate } from '@/utils/date';
import {
  ArrowLeft, Plus, Calendar, MapPin, Clock, ExternalLink,
  Trash2, Edit, RefreshCw, Users, Globe, Search, Pencil, Gift,
  Check, X, Layers, RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { authGet, authPost, authPut, authDelete } from '../../utils/auth';
import { safeHref } from '../../utils/url';
import regionsData from '@/data/regions.json';
import COUNTRY_NAMES from '@/data/countryNames';


const REGION_LIST = Object.entries(regionsData).map(([key, val]) => ({
  key, name: val.name, countries: val.countries,
}));

const EventsManagement = () => {
  const navigate = useNavigate();
  const [viewTab, setViewTab] = useState('groups'); // 'groups' | 'events' | 'holidays'
  const [activeTab, setActiveTab] = useState('upcoming'); // within 'events' tab: upcoming | past
  const [meetupGroups, setMeetupGroups] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [scrapingGroupId, setScrapingGroupId] = useState(null);
  
  // Group dialog state
  const [groupDialog, setGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({
    name: '',
    meetup_url: '',
    city: '',
    state: '',
    country: 'Australia',
    latitude: '',
    longitude: '',
    description: '',
    auto_scrape: true,
    scrape_interval_hours: 24
  });
  
  // Event dialog state
  const [eventDialog, setEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    meetup_group_id: '',
    title: '',
    description: '',
    event_url: '',
    event_date: '',
    event_time: '',
    end_time: '',
    venue_name: '',
    venue_address: '',
    city: '',
    state: '',
    country: 'Australia',
    latitude: '',
    longitude: '',
    is_bitcoin_event: false
  });
  
  // Bitcoin Holidays state
  const [bitcoinHolidays, setBitcoinHolidays] = useState([]);
  const [holidayDialog, setHolidayDialog] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayForm, setHolidayForm] = useState({
    month: '',
    day: '',
    title: '',
    description: '',
    event_url: 'https://bitcoin.holiday/'
  });
  
  // Discovery queue — candidates surfaced by the weekly rolling sweep
  // (n8n cron or Claude Code agent) awaiting admin approve/reject.
  const [candidates, setCandidates] = useState([]);
  const [candidateActionId, setCandidateActionId] = useState(null);

  // Meetup Submissions state
  const [meetupSubmissions, setMeetupSubmissions] = useState([]);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [submissionForm, setSubmissionForm] = useState({
    group_name: '',
    city: '',
    state: '',
    description: ''
  });
  const [refetchingId, setRefetchingId] = useState(null);
  const [approvingSubmissionId, setApprovingSubmissionId] = useState(null);
  const [rejectingSubmissionId, setRejectingSubmissionId] = useState(null);
  const [deletingSubmissionId, setDeletingSubmissionId] = useState(null);

  const [settingsDialog, setSettingsDialog] = useState(false);

  // URL import state
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // One-time auth gate + initial full load. Intentionally depends only on
  // `navigate` so flipping the Upcoming/Past sub-tab does NOT re-run auth or
  // refetch all five collections.
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authGet('/auth/me');
        if (!response.data || response.data.role !== 'admin') {
          navigate('/admin/login');
          return;
        }
        fetchData();
      } catch (error) {
        navigate('/admin/login');
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Refetch only the events list when the Upcoming/Past sub-tab changes.
  // Skipped on first mount — the auth effect's fetchData() already loaded it.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [groupsRes, eventsRes, holidaysRes, submissionsRes, candidatesRes] = await Promise.all([
        authGet('/admin/meetup-groups'),
        authGet(`/admin/events?tab=${activeTab}`),
        authGet('/admin/bitcoin-holidays'),
        authGet('/admin/meetup-submissions'),
        authGet('/admin/meetup-candidates?status=pending'),
      ]);
      setMeetupGroups(groupsRes.data);
      setEvents(eventsRes.data);
      setBitcoinHolidays(holidaysRes.data);
      setMeetupSubmissions(submissionsRes.data);
      setCandidates(candidatesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  // Scoped refetchers — used by CRUD handlers so a single edit/delete only
  // re-pulls the collection it touched instead of all five.
  const fetchEvents = async () => {
    try {
      const res = await authGet(`/admin/events?tab=${activeTab}`);
      setEvents(res.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await authGet('/admin/meetup-groups');
      setMeetupGroups(res.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchHolidays = async () => {
    try {
      const res = await authGet('/admin/bitcoin-holidays');
      setBitcoinHolidays(res.data);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };
  
  // Discovery queue handlers
  const handleApproveCandidate = async (candidateId) => {
    setCandidateActionId(candidateId);
    try {
      await authPost(`/admin/meetup-candidates/${candidateId}/approve`);
      toast.success('Candidate approved and added');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approve failed');
    }
    setCandidateActionId(null);
  };

  const handleRejectCandidate = async (candidateId) => {
    const reason = window.prompt('Rejection reason (optional):') || '';
    setCandidateActionId(candidateId);
    try {
      await authPost(`/admin/meetup-candidates/${candidateId}/reject`, { reason });
      toast.success('Candidate rejected');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reject failed');
    }
    setCandidateActionId(null);
  };

  // Group handlers
  const handleOpenGroupDialog = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setGroupForm({
        name: group.name || '',
        meetup_url: group.url || group.meetup_url || '',
        platform: group.platform || 'meetup',
        city: group.city || '',
        state: group.state || '',
        country: group.country || 'Australia',
        latitude: group.latitude || '',
        longitude: group.longitude || '',
        description: group.description || '',
        auto_scrape: group.auto_scrape !== false,
        scrape_interval_hours: group.scrape_interval_hours || 24
      });
    } else {
      setEditingGroup(null);
      setGroupForm({
        name: '',
        meetup_url: '',
        platform: 'meetup',
        city: '',
        state: '',
        country: 'Australia',
        latitude: '',
        longitude: '',
        description: '',
        auto_scrape: true,
        scrape_interval_hours: 24
      });
    }
    setGroupDialog(true);
  };
  
  const handleSaveGroup = async () => {
    if (!groupForm.name.trim()) {
      toast.error('Group name is required');
      return;
    }
    
    try {
      const payload = {
        ...groupForm,
        latitude: groupForm.latitude ? parseFloat(groupForm.latitude) : null,
        longitude: groupForm.longitude ? parseFloat(groupForm.longitude) : null
      };
      
      if (editingGroup) {
        await authPut(`/admin/meetup-groups/${editingGroup.id}`, payload);
        toast.success('Meetup group updated');
      } else {
        await authPost('/admin/meetup-groups', payload);
        toast.success('Meetup group created');
      }

      setGroupDialog(false);
      fetchGroups();
    } catch (error) {
      toast.error('Failed to save group');
    }
  };
  
  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Delete this meetup group and all its events?')) return;
    
    try {
      await authDelete(`/admin/meetup-groups/${groupId}`);
      toast.success('Group deleted');
      // Deleting a group cascades to its events, so refresh both.
      fetchGroups();
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };
  
  const handleScrapeGroup = async (groupId) => {
    setScrapingGroupId(groupId);
    try {
      const res = await authPost(`/admin/meetup-groups/${groupId}/scrape`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Scraping failed');
    }
    setScrapingGroupId(null);
  };
  
  const handleScrapeAll = async () => {
    setScraping(true);
    try {
      const res = await authPost('/admin/scrape-all-groups');
      const successCount = res.data.results.filter(r => !r.error).length;
      let message = `Scraped ${successCount} groups`;
      if (res.data.cross_platform_duplicates_merged > 0) {
        message += `, ${res.data.cross_platform_duplicates_merged} cross-platform duplicates merged`;
      }
      toast.success(message);
      fetchData();
    } catch (error) {
      toast.error('Scraping failed');
    }
    setScraping(false);
  };
  
  const [deduplicating, setDeduplicating] = useState(false);
  const [duplicatePreview, setDuplicatePreview] = useState(null);
  
  const handleCheckDuplicates = async () => {
    try {
      const res = await authGet('/admin/duplicate-events');
      setDuplicatePreview(res.data);
      if (res.data.total_duplicates === 0) {
        toast.success('No duplicates found across platforms');
      }
    } catch (error) {
      toast.error('Failed to check duplicates');
    }
  };
  
  const handleDeduplicate = async () => {
    setDeduplicating(true);
    try {
      const res = await authPost('/admin/deduplicate-events');
      toast.success(res.data.message);
      setDuplicatePreview(null);
      fetchData();
    } catch (error) {
      toast.error('Deduplication failed');
    }
    setDeduplicating(false);
  };
  
  // Submission handlers
  const handleRefetchSubmission = async (submissionId) => {
    setRefetchingId(submissionId);
    try {
      await authPost(`/admin/meetup-submissions/${submissionId}/refetch`);
      toast.success('Data re-fetched successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to refetch data: ' + (error.response?.data?.detail || 'Unknown error'));
    }
    setRefetchingId(null);
  };
  
  const handleEditSubmission = (submission) => {
    setEditingSubmission(submission);
    setSubmissionForm({
      group_name: submission.group_name || '',
      city: submission.city || '',
      state: submission.state || '',
      description: submission.description || ''
    });
  };
  
  const handleSaveSubmission = async () => {
    if (!editingSubmission) return;
    try {
      await authPut(`/admin/meetup-submissions/${editingSubmission.id}`, submissionForm);
      toast.success('Submission updated');
      setEditingSubmission(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update submission');
    }
  };
  
  const handleApproveSubmission = async (submission) => {
    // In-flight guard: the approve→create-group pair is two non-atomic
    // requests, so prevent a double-click from firing it twice.
    if (approvingSubmissionId) return;
    setApprovingSubmissionId(submission.id);
    let approved = false;
    try {
      // Approve and create a new group from submission
      await authPut(`/admin/meetup-submissions/${submission.id}`, { status: 'approved' });
      approved = true;
      // Create the group with platform
      await authPost('/admin/meetup-groups', {
        name: submission.group_name,
        meetup_url: submission.events_url,
        city: submission.city,
        state: submission.state,
        description: submission.description,
        platform: submission.platform || 'meetup',
        auto_scrape: true
      });
      toast.success('Submission approved and group created');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Unknown error';
      if (approved) {
        // The submission was marked approved but the group never got created.
        // Roll the status back so the queue isn't left in a broken half-state.
        let reverted = false;
        try {
          await authPut(`/admin/meetup-submissions/${submission.id}`, { status: 'pending' });
          reverted = true;
        } catch (revertErr) {
          console.error('Failed to revert submission status:', revertErr);
        }
        if (reverted) {
          toast.error(`Group creation failed — submission reverted to pending. ${detail}`);
        } else {
          toast.error(`Group creation failed and the submission could not be reverted — it may be stuck as approved. ${detail}`);
        }
        fetchData();
      } else {
        toast.error(`Failed to approve submission: ${detail}`);
      }
    }
    setApprovingSubmissionId(null);
  };
  
  const handleRejectSubmission = async (submissionId) => {
    // In-flight guard: prevent a double-click from firing the reject twice.
    if (rejectingSubmissionId) return;
    setRejectingSubmissionId(submissionId);
    try {
      await authPut(`/admin/meetup-submissions/${submissionId}`, { status: 'rejected' });
      toast.success('Submission rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject submission');
    }
    setRejectingSubmissionId(null);
  };

  const handleDeleteSubmission = async (submissionId) => {
    // In-flight guard: prevent a double-click from firing the delete twice.
    if (deletingSubmissionId) return;
    if (!window.confirm('Delete this submission permanently?')) return;
    setDeletingSubmissionId(submissionId);
    try {
      await authDelete(`/admin/meetup-submissions/${submissionId}`);
      toast.success('Submission deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete submission');
    }
    setDeletingSubmissionId(null);
  };
  
  // Event handlers
  const handleOpenEventDialog = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setEventForm({
        meetup_group_id: event.meetup_group_id || '',
        title: event.title || '',
        description: event.description || '',
        event_url: event.event_url || '',
        event_date: event.event_date || '',
        event_time: event.event_time || '',
        end_time: event.end_time || '',
        venue_name: event.venue_name || '',
        venue_address: event.venue_address || '',
        city: event.city || '',
        state: event.state || '',
        country: event.country || 'Australia',
        latitude: event.latitude || '',
        longitude: event.longitude || '',
        is_bitcoin_event: event.is_bitcoin_event || false
      });
    } else {
      setEditingEvent(null);
      setEventForm({
        meetup_group_id: meetupGroups[0]?.id || '',
        title: '',
        description: '',
        event_url: '',
        event_date: '',
        event_time: '',
        end_time: '',
        venue_name: '',
        venue_address: '',
        city: '',
        state: '',
        country: 'Australia',
        latitude: '',
        longitude: '',
        is_bitcoin_event: false
      });
    }
    setEventDialog(true);
  };
  
  const handleSaveEvent = async () => {
    if (!eventForm.title.trim() || !eventForm.event_date || !eventForm.meetup_group_id) {
      toast.error('Title, date, and meetup group are required');
      return;
    }
    
    try {
      const payload = {
        ...eventForm,
        latitude: eventForm.latitude ? parseFloat(eventForm.latitude) : null,
        longitude: eventForm.longitude ? parseFloat(eventForm.longitude) : null
      };
      
      if (editingEvent) {
        await authPut(`/admin/events/${editingEvent.id}`, payload);
        toast.success('Event updated');
      } else {
        await authPost('/admin/events', payload);
        toast.success('Event created');
      }

      setEventDialog(false);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to save event');
    }
  };
  
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    
    try {
      await authDelete(`/admin/events/${eventId}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };
  
  // Bitcoin Holidays handlers
  const handleOpenHolidayDialog = (holiday = null) => {
    if (holiday) {
      setEditingHoliday(holiday);
      setHolidayForm({
        month: holiday.month || '',
        day: holiday.day || '',
        title: holiday.title || '',
        description: holiday.description || '',
        event_url: holiday.event_url || 'https://bitcoin.holiday/'
      });
    } else {
      setEditingHoliday(null);
      setHolidayForm({
        month: '',
        day: '',
        title: '',
        description: '',
        event_url: 'https://bitcoin.holiday/'
      });
    }
    setHolidayDialog(true);
  };
  
  const handleSaveHoliday = async () => {
    if (!holidayForm.month || !holidayForm.day || !holidayForm.title.trim()) {
      toast.error('Month, day, and title are required');
      return;
    }
    
    try {
      const payload = {
        month: parseInt(holidayForm.month),
        day: parseInt(holidayForm.day),
        title: holidayForm.title.trim(),
        description: holidayForm.description || '',
        event_url: holidayForm.event_url || 'https://bitcoin.holiday/'
      };
      
      if (editingHoliday) {
        await authPut(`/admin/bitcoin-holidays/${editingHoliday.id}`, payload);
        toast.success('Bitcoin Holiday updated');
      } else {
        await authPost('/admin/bitcoin-holidays', payload);
        toast.success('Bitcoin Holiday created');
      }

      setHolidayDialog(false);
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save holiday');
    }
  };
  
  const handleDeleteHoliday = async (holidayId) => {
    if (!window.confirm('Delete this Bitcoin Holiday?')) return;
    
    try {
      await authDelete(`/admin/bitcoin-holidays/${holidayId}`);
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };
  
  const getMonthName = (month) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[month - 1] || '';
  };
  
  // Region/Country/Group filter state
  const [eventsRegion, setEventsRegion] = useState('');
  const [eventsCountry, setEventsCountry] = useState('');
  const [eventsGroupFilter, setEventsGroupFilter] = useState('');
  // Bitcoin events are hidden from the default list. Toggle this on so admins
  // can see/edit/untoggle events that were flagged as Bitcoin events.
  const [showBitcoinEvents, setShowBitcoinEvents] = useState(false);

  // Meetup-groups filter state (separate from events filter — admins expect
  // to narrow the Active Groups grid the same way they filter events).
  const [groupsRegion, setGroupsRegion] = useState('');
  const [groupsCountry, setGroupsCountry] = useState('');

  // Cascading country options for admin filters
  const adminCountryOptions = useMemo(() => {
    if (!eventsRegion) {
      const allCodes = Object.values(regionsData).flatMap(r => r.countries);
      return [...new Set(allCodes)]
        .map(code => ({ code, name: COUNTRY_NAMES[code] || code }))
        .filter(c => c.name)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    const regionEntry = regionsData[eventsRegion];
    if (!regionEntry) return [];
    return regionEntry.countries
      .map(code => ({ code, name: COUNTRY_NAMES[code] || code }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [eventsRegion]);

  // Filter events by type + admin filters. Memoized so it only recomputes
  // when the source events or any filter actually changes.
  const meetupEvents = useMemo(() => {
    let f = showBitcoinEvents ? events : events.filter(e => !e.is_bitcoin_event);
    if (eventsRegion) {
      f = f.filter(e => (e.region || '').toLowerCase() === eventsRegion.toLowerCase());
    }
    if (eventsCountry) {
      f = f.filter(e => (e.country || 'AU').toUpperCase() === eventsCountry);
    }
    if (eventsGroupFilter.trim()) {
      const q = eventsGroupFilter.trim().toLowerCase();
      f = f.filter(e => (e.meetup_group_name || '').toLowerCase().includes(q));
    }
    return f;
  }, [events, eventsRegion, eventsCountry, eventsGroupFilter, showBitcoinEvents]);

  // Count of events flagged as Bitcoin events (hidden by default).
  const bitcoinEventCount = useMemo(
    () => events.filter(e => e.is_bitcoin_event).length,
    [events]
  );

  // Cascading country options for the meetup-groups filter row. Matches the
  // shape of `adminCountryOptions` but keyed off `groupsRegion`.
  const groupsCountryOptions = useMemo(() => {
    if (!groupsRegion) {
      const allCodes = Object.values(regionsData).flatMap(r => r.countries);
      return [...new Set(allCodes)]
        .map(code => ({ code, name: COUNTRY_NAMES[code] || code }))
        .filter(c => c.name)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    const regionEntry = regionsData[groupsRegion];
    if (!regionEntry) return [];
    return regionEntry.countries
      .map(code => ({ code, name: COUNTRY_NAMES[code] || code }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [groupsRegion]);

  // Resolve which UN-5 region a group belongs to by looking its country_code
  // up in regions.json. Backend only persists country_code on the group, not
  // the region slug — this keeps the filter truthy even for legacy rows.
  const groupRegionFor = (group) => {
    const cc = (group.country_code || '').toUpperCase();
    if (!cc) return '';
    for (const [slug, entry] of Object.entries(regionsData)) {
      if (entry.countries.includes(cc)) return slug;
    }
    return '';
  };

  const filteredMeetupGroups = useMemo(() => meetupGroups.filter(g => {
    if (groupsRegion && groupRegionFor(g) !== groupsRegion) return false;
    if (groupsCountry && (g.country_code || '').toUpperCase() !== groupsCountry) return false;
    return true;
  }), [meetupGroups, groupsRegion, groupsCountry]);
  
  const handleImportUrl = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    setImportResult(null);
    try {
      const res = await authPost('/admin/events/import-url', { url: importUrl.trim() });
      setImportResult({ ok: true, data: res.data });
      setImportUrl('');
      fetchData();
    } catch (err) {
      setImportResult({ ok: false, error: err.response?.data?.detail || 'Import failed' });
    }
    setImporting(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = parseLocalDate(dateStr);
    return date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };
  
  // Scheduler Status Component
  const SchedulerStatus = () => {
    const [status, setStatus] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    
    const fetchStatus = async () => {
      try {
        const res = await authGet('/admin/scheduler/status');
        setStatus(res.data);
      } catch (error) {
        console.error('Error fetching scheduler status:', error);
      }
      setLoadingStatus(false);
    };
    
    useEffect(() => {
      fetchStatus();
      const interval = setInterval(fetchStatus, 30000); // Refresh every 30s
      return () => clearInterval(interval);
    }, []);
    
    const handleStart = async () => {
      setActionLoading(true);
      try {
        await authPost('/admin/scheduler/start');
        toast.success('Scheduler started');
        fetchStatus();
      } catch (error) {
        toast.error('Failed to start scheduler');
      }
      setActionLoading(false);
    };
    
    const handleStop = async () => {
      setActionLoading(true);
      try {
        await authPost('/admin/scheduler/stop');
        toast.success('Scheduler stopped');
        fetchStatus();
      } catch (error) {
        toast.error('Failed to stop scheduler');
      }
      setActionLoading(false);
    };
    
    const handleTrigger = async () => {
      setActionLoading(true);
      try {
        const res = await authPost('/admin/scheduler/trigger');
        toast.success(`Scraped ${res.data.scraped} groups, added ${res.data.events_added} events`);
        fetchStatus();
        fetchData(); // Refresh main data
      } catch (error) {
        toast.error('Failed to trigger scrape');
      }
      setActionLoading(false);
    };
    
    const handleSetInterval = async (hours) => {
      try {
        await authPost('/admin/scheduler/interval', { hours });
        toast.success(`Interval set to ${hours} hours`);
        fetchStatus();
      } catch (error) {
        toast.error('Failed to set interval');
      }
    };
    
    if (loadingStatus) {
      return <div className="text-sm text-muted-foreground">Loading scheduler status...</div>;
    }
    
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${status?.running ? 'bg-green-500' : 'bg-muted-foreground'}`} />
            <span className="text-sm font-medium">
              {status?.running ? 'Running' : 'Stopped'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {status?.running ? (
              <Button size="sm" variant="outline" onClick={handleStop} disabled={actionLoading}>
                Stop
              </Button>
            ) : (
              <Button size="sm" onClick={handleStart} disabled={actionLoading}>
                Start
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={handleTrigger} disabled={actionLoading}>
              <RefreshCw className={`w-3 h-3 mr-1 ${actionLoading ? 'animate-spin' : ''}`} />
              Run Now
            </Button>
          </div>
        </div>
        
        <div className="text-xs text-muted-foreground space-y-1">
          <p>Interval: Every {status?.interval_hours || 24} hours</p>
          <p>Enabled groups: {status?.enabled_groups || 0}</p>
          {status?.last_scrape && <p>Last scrape: {new Date(status.last_scrape).toLocaleString()}</p>}
          {status?.next_scrape && status?.running && <p>Next scrape: {new Date(status.next_scrape).toLocaleString()}</p>}
        </div>

        <div className="flex items-center gap-2 pt-2">
          <span className="text-xs text-muted-foreground">Quick interval:</span>
          {[6, 12, 24].map(h => (
            <button
              key={h}
              onClick={() => handleSetInterval(h)}
              className={`text-xs px-2 py-1 rounded ${status?.interval_hours === h ? 'bg-primary text-white' : 'bg-muted hover:bg-muted/80'}`}
            >
              {h}h
            </button>
          ))}
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <h1 className="text-2xl font-heading font-bold text-foreground">
                Events Management
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setSettingsDialog(true)}>
                Scrape Settings
              </Button>
              <Button 
                variant="outline" 
                onClick={handleCheckDuplicates}
                title="Check for duplicate events across platforms"
              >
                <Layers className="w-4 h-4 mr-2" />
                Deduplicate
              </Button>
              <Button 
                variant="outline" 
                onClick={handleScrapeAll}
                disabled={scraping}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${scraping ? 'animate-spin' : ''}`} />
                Scrape All
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Primary view tabs */}
        <div className="flex border border-border rounded-lg overflow-hidden mb-6 w-fit">
          {[
            { id: 'groups', label: 'Meetup Groups', icon: Users },
            { id: 'events', label: 'Group Events', icon: Calendar },
            { id: 'holidays', label: 'Holidays', icon: Gift },
          ].map(t => {
            const Icon = t.icon;
            const active = viewTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setViewTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                  active ? 'bg-primary text-white' : 'bg-card text-muted-foreground hover:bg-muted'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Discovery Queue — pending candidates surfaced by the weekly
            rolling sweep (n8n cron / Claude Code agent). Only rendered
            when there's something to review to stay out of the way. */}
        {viewTab === 'groups' && candidates.length > 0 && (
          <div className="bg-card border border-border rounded-xl p-6 mb-8">
            <h2 className="text-xl font-heading font-semibold flex items-center gap-2 mb-4">
              <Search className="w-5 h-5 text-primary" />
              Discovery Queue
              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full">
                {candidates.length} pending
              </span>
            </h2>
            <p className="text-xs text-muted-foreground mb-4">
              Candidates surfaced by the weekly region sweep. Approve to add through the normal pipeline (URL validation + first scrape + thumbnail). Reject to block the URL from future sweeps.
            </p>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {candidates.map(c => (
                <div key={c.id} className="border border-border rounded-lg p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[c.city, c.country_code, c.region].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    {safeHref(c.url) && (
                      <a href={safeHref(c.url)} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex-shrink-0">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground font-mono truncate">{c.url}</div>
                  {c.description && (
                    <div className="text-xs text-muted-foreground line-clamp-2">{c.description}</div>
                  )}
                  <div className="flex gap-2 pt-1">
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => handleApproveCandidate(c.id)}
                      disabled={candidateActionId === c.id}
                      className="flex-1 h-8"
                    >
                      <Check className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRejectCandidate(c.id)}
                      disabled={candidateActionId === c.id}
                      className="flex-1 h-8"
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    source: {c.source || '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Meetup Groups Section - Combined with Submissions */}
        {viewTab === 'groups' && (
        <div className="bg-card border border-border rounded-xl p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-heading font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Meetup Groups
              {meetupSubmissions.filter(s => s.status === 'pending').length > 0 && (
                <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-0.5 rounded-full">
                  {meetupSubmissions.filter(s => s.status === 'pending').length} pending review
                </span>
              )}
            </h2>
            <Button onClick={() => handleOpenGroupDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Group
            </Button>
          </div>
          
          {/* Import from URL */}
          <div className="mb-5 p-4 bg-muted/50 rounded-lg border border-border">
            <p className="text-xs font-medium text-muted-foreground mb-2">Import from meetup.com URL (group or single event)</p>
            <div className="flex gap-2">
              <Input
                placeholder="https://www.meetup.com/your-group-name/"
                value={importUrl}
                onChange={e => { setImportUrl(e.target.value); setImportResult(null); }}
                onKeyDown={e => e.key === 'Enter' && handleImportUrl()}
                className="flex-1 text-sm"
                disabled={importing}
              />
              <Button size="sm" onClick={handleImportUrl} disabled={importing || !importUrl.trim()}>
                {importing ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Import'}
              </Button>
            </div>
            {importResult && (
              <div className={`mt-2 text-xs rounded-md px-3 py-2 ${importResult.ok ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'}`}>
                {importResult.ok
                  ? `✓ ${importResult.data.group_name} — ${importResult.data.events_added} new event(s), ${importResult.data.events_updated} updated`
                  : `✗ ${importResult.error}`}
              </div>
            )}
          </div>

          {/* Pending Submissions - Shown First */}
          {meetupSubmissions.filter(s => s.status === 'pending').length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Pending Review ({meetupSubmissions.filter(s => s.status === 'pending').length})
              </h3>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {meetupSubmissions.filter(s => s.status === 'pending').map(submission => {
                  const hasIncompleteData = !submission.group_name || submission.group_name === 'Unknown Group' || !submission.city;
                  const isWalk = submission.platform === 'bitcoin_walk';
                  
                  return (
                    <div 
                      key={submission.id} 
                      className={`border-2 border-dashed rounded-lg p-4 transition-colors ${
                        hasIncompleteData 
                          ? 'border-orange-300 bg-orange-50'
                          : isWalk
                            ? 'border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                            : 'border-yellow-300 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-semibold truncate ${hasIncompleteData ? 'text-orange-700' : 'text-foreground'}`}>
                            {submission.group_name || 'Unknown Group'}
                          </h3>
                          <span className="text-xs bg-yellow-200 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 px-2 py-0.5 rounded-full">
                            Pending Review
                          </span>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            title="Re-fetch data"
                            disabled={refetchingId === submission.id}
                            onClick={() => handleRefetchSubmission(submission.id)}
                          >
                            <RotateCcw className={`w-4 h-4 ${refetchingId === submission.id ? 'animate-spin' : ''}`} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            title="Edit"
                            onClick={() => handleEditSubmission(submission)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="text-sm text-muted-foreground space-y-1">
                        {(submission.city || submission.state) ? (
                          <p className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {[submission.city, submission.state].filter(Boolean).join(', ')}
                          </p>
                        ) : (
                          <p className="flex items-center gap-1 text-orange-600">
                            <MapPin className="w-3 h-3" />
                            No location set
                          </p>
                        )}
                        {submission.events_url && (
                          safeHref(submission.events_url) ? (
                            <a
                              href={safeHref(submission.events_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-primary hover:underline truncate"
                            >
                              <Globe className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{submission.events_url.replace(/^https?:\/\/(www\.)?/, '')}</span>
                            </a>
                          ) : (
                            <span className="flex items-center gap-1 text-muted-foreground truncate">
                              <Globe className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{submission.events_url.replace(/^https?:\/\/(www\.)?/, '')}</span>
                            </span>
                          )
                        )}
                        <p className="text-xs text-muted-foreground">
                          Submitted {new Date(submission.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      
                      {/* Platform Badge */}
                      <div className="mt-2 flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          isWalk 
                            ? 'bg-green-200 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                            : submission.platform === 'luma'
                              ? 'bg-pink-100 text-pink-700'
                              : submission.platform === 'eventbrite'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        }`}>
                          {isWalk ? 'Bitcoin Walk' 
                            : submission.platform === 'luma' ? 'Lu.ma'
                            : submission.platform === 'eventbrite' ? 'Eventbrite'
                            : submission.platform === 'meetup' ? 'Meetup'
                            : submission.platform || 'Other'}
                        </span>
                      </div>
                      
                      {/* Warning for incomplete data */}
                      {hasIncompleteData && (
                        <p className="text-xs text-orange-600 mt-2">
                          ⚠️ Incomplete - Refetch or Edit first
                        </p>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="mt-3 pt-3 border-t border-border flex justify-between items-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRejectSubmission(submission.id)}
                          disabled={rejectingSubmissionId === submission.id}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          {rejectingSubmissionId === submission.id ? (
                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <X className="w-4 h-4 mr-1" />
                          )}
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleApproveSubmission(submission)}
                          disabled={hasIncompleteData || approvingSubmissionId === submission.id}
                          className={isWalk ? 'bg-green-600 hover:bg-green-700' : 'bg-primary hover:bg-primary/90'}
                        >
                          {approvingSubmissionId === submission.id ? (
                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4 mr-1" />
                          )}
                          Approve
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Active Groups */}
          <div>
            {meetupSubmissions.filter(s => s.status === 'pending').length > 0 && (
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Check className="w-4 h-4" />
                Active Groups ({filteredMeetupGroups.length}{filteredMeetupGroups.length !== meetupGroups.length ? ` of ${meetupGroups.length}` : ''})
              </h3>
            )}

            {/* Region + Country filter row */}
            {meetupGroups.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Select value={groupsRegion || 'all'} onValueChange={v => { setGroupsRegion(v === 'all' ? '' : v); setGroupsCountry(''); }}>
                  <SelectTrigger className="w-[180px] h-9">
                    <SelectValue placeholder="All regions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All regions</SelectItem>
                    {REGION_LIST.map(r => (
                      <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={groupsCountry || 'all'} onValueChange={v => setGroupsCountry(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[200px] h-9">
                    <SelectValue placeholder="All countries" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="all">All countries</SelectItem>
                    {groupsCountryOptions.map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(groupsRegion || groupsCountry) && (
                  <Button variant="ghost" size="sm" onClick={() => { setGroupsRegion(''); setGroupsCountry(''); }}>
                    <X className="w-3.5 h-3.5 mr-1" /> Clear
                  </Button>
                )}
              </div>
            )}

            {meetupGroups.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No meetup groups yet. Add one to start tracking events.
              </p>
            ) : filteredMeetupGroups.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No groups match the current region/country filter.
              </p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredMeetupGroups.map(group => {
                  const isWalk = group.platform === 'bitcoin_walk';
                  
                  return (
                    <div 
                      key={group.id} 
                      className={`border rounded-lg p-4 transition-colors ${
                        isWalk 
                          ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 hover:border-green-400' 
                          : 'border-border hover:border-primary'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className={`font-semibold ${isWalk ? 'text-green-800' : 'text-foreground'}`}>
                          {group.name}
                        </h3>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleScrapeGroup(group.id)}
                            disabled={scrapingGroupId === group.id || !group.meetup_url}
                            title="Scrape events"
                          >
                            <RefreshCw className={`w-4 h-4 ${scrapingGroupId === group.id ? 'animate-spin' : ''}`} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenGroupDialog(group)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteGroup(group.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="text-sm text-muted-foreground space-y-1">
                        {group.city && (
                          <p className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {group.city}{group.state ? `, ${group.state}` : ''}
                          </p>
                        )}
                        {(group.meetup_url || group.url) && (
                          safeHref(group.meetup_url || group.url) ? (
                            <a
                              href={safeHref(group.meetup_url || group.url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-1 hover:underline ${isWalk ? 'text-green-700' : 'text-primary'}`}
                            >
                              <Globe className="w-3 h-3" />
                              {isWalk ? 'Bitcoin Walk Page' : 'Event Page'}
                            </a>
                          ) : (
                            <span className={`flex items-center gap-1 ${isWalk ? 'text-green-700' : 'text-muted-foreground'}`}>
                              <Globe className="w-3 h-3" />
                              {isWalk ? 'Bitcoin Walk Page' : 'Event Page'}
                            </span>
                          )
                        )}
                        {group.last_scraped_at && (
                          <p className="text-xs">
                            Last scraped: {formatDate(group.last_scraped_at)}
                          </p>
                        )}
                      </div>
                      
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${group.auto_scrape ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-muted text-muted-foreground'}`}>
                          {group.auto_scrape ? 'Auto-scrape ON' : 'Manual only'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          isWalk 
                            ? 'bg-green-200 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                            : group.platform === 'luma'
                              ? 'bg-pink-100 text-pink-700'
                              : group.platform === 'eventbrite'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        }`}>
                          {isWalk ? 'Bitcoin Walk' 
                            : group.platform === 'luma' ? 'Lu.ma'
                            : group.platform === 'eventbrite' ? 'Eventbrite'
                            : 'Meetup'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        )}

        {/* Bitcoin Holidays Section */}
        {viewTab === 'holidays' && (
        <div className="bg-card border border-orange-200 dark:border-orange-800 rounded-xl p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-heading font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-500" />
              <span className="text-orange-600">Bitcoin Holidays</span>
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">{bitcoinHolidays.length} holidays</span>
            </h2>
            <Button onClick={() => handleOpenHolidayDialog()} className="bg-orange-500 hover:bg-orange-600">
              <Plus className="w-4 h-4 mr-2" />
              Add Holiday
            </Button>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            Bitcoin Holidays are historical anniversaries that appear in orange on the public calendar. They recur every year on the same date.
          </p>
          
          {bitcoinHolidays.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No Bitcoin Holidays found. They will be loaded from the default data on first access.
            </p>
          ) : (
            <div className="grid gap-2 max-h-[400px] overflow-y-auto">
              {bitcoinHolidays.map(holiday => (
                <div key={holiday.id} className="border border-orange-200 bg-orange-50 rounded-lg p-3 hover:border-orange-400 transition-colors">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded min-w-[60px] text-center">
                        {getMonthName(holiday.month)} {holiday.day}
                      </div>
                      <div>
                        <h3 className="font-medium text-foreground">{holiday.title}</h3>
                        {holiday.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1">{holiday.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {holiday.event_url && (
                        safeHref(holiday.event_url) ? (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={safeHref(holiday.event_url)} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" disabled title="Invalid link">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        )
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleOpenHolidayDialog(holiday)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteHoliday(holiday.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Events Section */}
        {viewTab === 'events' && (
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-heading font-semibold flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Group Events
              </h2>
              
              {/* Tabs */}
              <div className="flex border border-border rounded-lg overflow-hidden">
                <button
                  onClick={() => setActiveTab('upcoming')}
                  className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                    activeTab === 'upcoming' 
                      ? 'bg-primary text-white' 
                      : 'bg-card text-muted-foreground hover:bg-muted'
                  }`}
                >
                  Upcoming
                </button>
                <button
                  onClick={() => setActiveTab('past')}
                  className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                    activeTab === 'past' 
                      ? 'bg-primary text-white' 
                      : 'bg-card text-muted-foreground hover:bg-muted'
                  }`}
                >
                  Past
                </button>
              </div>
            </div>
            
            <Button onClick={() => handleOpenEventDialog()} disabled={meetupGroups.length === 0}>
              <Plus className="w-4 h-4 mr-2" />
              Add Event
            </Button>
          </div>

          {/* Event filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            <select
              value={eventsRegion}
              onChange={(e) => { setEventsRegion(e.target.value); setEventsCountry(''); }}
              className="px-3 py-1.5 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 min-w-[120px]"
              aria-label="Filter events by region"
            >
              <option value="">All Regions</option>
              {REGION_LIST.map(r => (
                <option key={r.key} value={r.key}>{r.name}</option>
              ))}
            </select>
            <select
              value={eventsCountry}
              onChange={(e) => setEventsCountry(e.target.value)}
              className="px-3 py-1.5 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 min-w-[140px]"
              aria-label="Filter events by country"
            >
              <option value="">All Countries</option>
              {adminCountryOptions.map(c => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Group name..."
              value={eventsGroupFilter}
              onChange={(e) => setEventsGroupFilter(e.target.value)}
              className="px-3 py-1.5 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 w-44"
            />
            <label className="flex items-center gap-1.5 px-1 text-sm text-foreground whitespace-nowrap cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showBitcoinEvents}
                onChange={(e) => setShowBitcoinEvents(e.target.checked)}
                className="rounded border-border"
              />
              Show Bitcoin events ({bitcoinEventCount})
            </label>
            {(eventsRegion || eventsCountry || eventsGroupFilter || showBitcoinEvents) && (
              <button
                onClick={() => { setEventsRegion(''); setEventsCountry(''); setEventsGroupFilter(''); setShowBitcoinEvents(false); }}
                className="text-xs text-primary hover:underline whitespace-nowrap"
              >
                Clear filters
              </button>
            )}
          </div>

          {meetupEvents.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No {activeTab} events found for the selected filters.
            </p>
          ) : (
            <div className="space-y-3">
              {meetupEvents.map(event => (
                <div key={event.id} className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-foreground">{event.title}</h3>
                        {event.is_manual && (
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full">Manual</span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(event.event_date)}
                          {event.event_time && ` at ${event.event_time}`}
                        </span>
                        
                        {event.venue_name && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {event.venue_name}
                          </span>
                        )}
                        
                        <span className="text-xs text-primary">
                          {event.meetup_group_name}
                        </span>
                      </div>
                      
                      {event.event_url && (
                        safeHref(event.event_url) ? (
                          <a
                            href={safeHref(event.event_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Event
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <ExternalLink className="w-3 h-3" />
                            View Event (invalid link)
                          </span>
                        )
                      )}
                    </div>
                    
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenEventDialog(event)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteEvent(event.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Edit Submission Dialog */}
      <Dialog open={!!editingSubmission} onOpenChange={() => setEditingSubmission(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Submission</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Group Name *</Label>
              <Input
                value={submissionForm.group_name}
                onChange={(e) => setSubmissionForm({...submissionForm, group_name: e.target.value})}
                placeholder="Bitcoin Sydney"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={submissionForm.city}
                  onChange={(e) => setSubmissionForm({...submissionForm, city: e.target.value})}
                  placeholder="Sydney"
                />
              </div>
              <div>
                <Label>State</Label>
                <Select 
                  value={submissionForm.state}
                  onValueChange={(val) => setSubmissionForm({...submissionForm, state: val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NSW">NSW</SelectItem>
                    <SelectItem value="VIC">VIC</SelectItem>
                    <SelectItem value="QLD">QLD</SelectItem>
                    <SelectItem value="SA">SA</SelectItem>
                    <SelectItem value="WA">WA</SelectItem>
                    <SelectItem value="TAS">TAS</SelectItem>
                    <SelectItem value="NT">NT</SelectItem>
                    <SelectItem value="ACT">ACT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={submissionForm.description}
                onChange={(e) => setSubmissionForm({...submissionForm, description: e.target.value})}
                placeholder="Brief description"
              />
            </div>
            
            {editingSubmission?.events_url && (
              <p className="text-xs text-muted-foreground">
                URL: {editingSubmission.events_url}
              </p>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setEditingSubmission(null)}>Cancel</Button>
              <Button onClick={handleSaveSubmission} className="bg-primary">Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Group Dialog */}
      <Dialog open={groupDialog} onOpenChange={setGroupDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Edit Meetup Group' : 'Add Meetup Group'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Group Name *</Label>
              <Input
                value={groupForm.name}
                onChange={(e) => setGroupForm({...groupForm, name: e.target.value})}
                placeholder="Bitcoin Sydney"
              />
            </div>
            
            <div>
              <Label>URL (for scraping)</Label>
              <Input
                value={groupForm.meetup_url}
                onChange={(e) => setGroupForm({...groupForm, meetup_url: e.target.value})}
                placeholder={groupForm.platform === 'bitcoin_walk' 
                  ? "https://bitcoinwalk.org/location-name" 
                  : "https://www.meetup.com/bitcoin_sydney/"}
              />
            </div>
            
            <div>
              <Label>Platform</Label>
              <Select 
                value={groupForm.platform} 
                onValueChange={(val) => setGroupForm({...groupForm, platform: val})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="meetup">Meetup.com</SelectItem>
                  <SelectItem value="bitcoin_walk">Bitcoin Walk</SelectItem>
                  <SelectItem value="luma">Lu.ma</SelectItem>
                  <SelectItem value="eventbrite">Eventbrite</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={groupForm.city}
                  onChange={(e) => setGroupForm({...groupForm, city: e.target.value})}
                  placeholder="Sydney"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={groupForm.state}
                  onChange={(e) => setGroupForm({...groupForm, state: e.target.value})}
                  placeholder="NSW"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Latitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={groupForm.latitude}
                  onChange={(e) => setGroupForm({...groupForm, latitude: e.target.value})}
                  placeholder="-33.8688"
                />
              </div>
              <div>
                <Label>Longitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={groupForm.longitude}
                  onChange={(e) => setGroupForm({...groupForm, longitude: e.target.value})}
                  placeholder="151.2093"
                />
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={groupForm.description}
                onChange={(e) => setGroupForm({...groupForm, description: e.target.value})}
                placeholder="Monthly Bitcoin meetup in Sydney"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={groupForm.auto_scrape}
                  onCheckedChange={(val) => setGroupForm({...groupForm, auto_scrape: val})}
                />
                <Label>Auto-scrape events</Label>
              </div>
              
              {groupForm.auto_scrape && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Every</Label>
                  <Input
                    type="number"
                    className="w-20"
                    value={groupForm.scrape_interval_hours}
                    onChange={(e) => setGroupForm({...groupForm, scrape_interval_hours: parseInt(e.target.value) || 24})}
                  />
                  <span className="text-sm text-muted-foreground">hours</span>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setGroupDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveGroup}>{editingGroup ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Event Dialog */}
      <Dialog open={eventDialog} onOpenChange={setEventDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'Add Event'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Meetup Group *</Label>
              <select
                className="w-full border border-border rounded-md px-3 py-2"
                value={eventForm.meetup_group_id}
                onChange={(e) => setEventForm({...eventForm, meetup_group_id: e.target.value})}
              >
                <option value="">Select a group...</option>
                {meetupGroups.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label>Event Title *</Label>
              <Input
                value={eventForm.title}
                onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                placeholder="Monthly Bitcoin Meetup"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Event Date *</Label>
                <Input
                  type="date"
                  value={eventForm.event_date}
                  onChange={(e) => setEventForm({...eventForm, event_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={eventForm.event_time}
                  onChange={(e) => setEventForm({...eventForm, event_time: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label>Event URL</Label>
              <Input
                value={eventForm.event_url}
                onChange={(e) => setEventForm({...eventForm, event_url: e.target.value})}
                placeholder="https://meetup.com/event/..."
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={eventForm.is_bitcoin_event}
                onCheckedChange={(val) => setEventForm({...eventForm, is_bitcoin_event: val})}
              />
              <Label>Bitcoin event (shows in orange on the public calendar)</Label>
            </div>

            <div>
              <Label>Venue Name</Label>
              <Input
                value={eventForm.venue_name}
                onChange={(e) => setEventForm({...eventForm, venue_name: e.target.value})}
                placeholder="The Local Pub"
              />
            </div>
            
            <div>
              <Label>Venue Address</Label>
              <Input
                value={eventForm.venue_address}
                onChange={(e) => setEventForm({...eventForm, venue_address: e.target.value})}
                placeholder="123 George St"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={eventForm.city}
                  onChange={(e) => setEventForm({...eventForm, city: e.target.value})}
                  placeholder="Sydney"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={eventForm.state}
                  onChange={(e) => setEventForm({...eventForm, state: e.target.value})}
                  placeholder="NSW"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Latitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={eventForm.latitude}
                  onChange={(e) => setEventForm({...eventForm, latitude: e.target.value})}
                />
              </div>
              <div>
                <Label>Longitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={eventForm.longitude}
                  onChange={(e) => setEventForm({...eventForm, longitude: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <textarea
                className="w-full border border-border rounded-md px-3 py-2 min-h-[80px]"
                value={eventForm.description}
                onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
                placeholder="Event description..."
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setEventDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveEvent}>{editingEvent ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Settings Dialog */}
      <Dialog open={settingsDialog} onOpenChange={setSettingsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Event Scraper</DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <SchedulerStatus />
            </div>

            <div className="flex justify-end pt-4">
              <Button variant="outline" onClick={() => setSettingsDialog(false)}>Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Bitcoin Holiday Dialog */}
      <Dialog open={holidayDialog} onOpenChange={setHolidayDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
              {editingHoliday ? 'Edit Bitcoin Holiday' : 'Add Bitcoin Holiday'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-700">
              Bitcoin Holidays are recurring anniversaries that appear on the same date every year in orange on the public calendar.
            </div>
            
            <div>
              <Label>Holiday Title *</Label>
              <Input
                value={holidayForm.title}
                onChange={(e) => setHolidayForm({...holidayForm, title: e.target.value})}
                placeholder="Bitcoin Pizza Day"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Month *</Label>
                <select
                  className="w-full border border-border rounded-md px-3 py-2"
                  value={holidayForm.month}
                  onChange={(e) => setHolidayForm({...holidayForm, month: e.target.value})}
                >
                  <option value="">Select month...</option>
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
              </div>
              <div>
                <Label>Day *</Label>
                <Input
                  type="number"
                  min="1"
                  max="31"
                  value={holidayForm.day}
                  onChange={(e) => setHolidayForm({...holidayForm, day: e.target.value})}
                  placeholder="22"
                />
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <textarea
                className="w-full border border-border rounded-md px-3 py-2 min-h-[80px]"
                value={holidayForm.description}
                onChange={(e) => setHolidayForm({...holidayForm, description: e.target.value})}
                placeholder="The first real-world Bitcoin transaction..."
              />
            </div>
            
            <div>
              <Label>Event URL</Label>
              <Input
                value={holidayForm.event_url}
                onChange={(e) => setHolidayForm({...holidayForm, event_url: e.target.value})}
                placeholder="https://bitcoin.holiday/"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setHolidayDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveHoliday} className="bg-orange-500 hover:bg-orange-600">
                {editingHoliday ? 'Update' : 'Create'} Holiday
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Deduplication Preview Dialog */}
      <Dialog open={!!duplicatePreview} onOpenChange={() => setDuplicatePreview(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-orange-500" />
              Cross-Platform Duplicate Events
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {duplicatePreview?.total_duplicates === 0 ? (
              <div className="text-center py-8">
                <Check className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="text-muted-foreground">No duplicate events found across platforms.</p>
                <p className="text-sm text-muted-foreground mt-1">Your events are all unique!</p>
              </div>
            ) : (
              <>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-sm text-orange-800">
                    Found <strong>{duplicatePreview?.total_duplicates}</strong> potential duplicate events 
                    across <strong>{duplicatePreview?.total_groups}</strong> group(s). 
                    Merging will keep the event with the most complete data and store alternate URLs.
                  </p>
                </div>
                
                {duplicatePreview?.duplicate_groups?.map((group, index) => (
                  <div key={index} className="border border-border rounded-lg p-4">
                    <div className="mb-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded">PRIMARY</span>
                        <span className="font-semibold">{group.primary_event.title}</span>
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {group.primary_event.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {group.primary_event.city || 'Unknown'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="border-t border-border pt-3 space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">DUPLICATES:</p>
                      {group.duplicates.map((dup, dupIndex) => (
                        <div key={dupIndex} className="flex justify-between items-center bg-muted rounded p-2">
                          <div>
                            <span className="text-sm font-medium">{dup.title}</span>
                            <div className="text-xs text-muted-foreground">
                              {dup.similarity}% title match
                            </div>
                          </div>
                          <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2 py-0.5 rounded">
                            Will be merged
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                <div className="flex justify-end gap-2 pt-4 border-t border-border">
                  <Button variant="outline" onClick={() => setDuplicatePreview(null)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleDeduplicate}
                    disabled={deduplicating}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    {deduplicating ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Merging...
                      </>
                    ) : (
                      <>
                        <Layers className="w-4 h-4 mr-2" />
                        Merge {duplicatePreview?.total_duplicates} Duplicates
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventsManagement;
